import { getAppConfigApi, type AppConfig } from './api';

const CACHE_TTL_MS = 5 * 60 * 1000;

let cached: AppConfig | null = null;
let cachedAt: number = 0;

export async function getAppConfig(forceRefresh = false): Promise<AppConfig | null> {
  if (!forceRefresh && cached != null && Date.now() - cachedAt < CACHE_TTL_MS) {
    return cached;
  }
  const result = await getAppConfigApi();
  if (result.error || result.config == null) {
    if (cached != null) return cached;
    return null;
  }
  cached = result.config;
  cachedAt = Date.now();
  return cached;
}

export function invalidateAppConfigCache(): void {
  cached = null;
  cachedAt = 0;
}

export function getAppDisplayName(config: AppConfig | null): string {
  return config?.appDisplayName?.trim() ?? '';
}

export function getSupportEmail(config: AppConfig | null): string {
  return config?.supportEmail?.trim() ?? '';
}

export function getTermsUrl(config: AppConfig | null): string {
  return config?.termsUrl?.trim() ?? '';
}

export function getPrivacyUrl(config: AppConfig | null): string {
  return config?.privacyUrl?.trim() ?? '';
}

export function getHelpUrl(config: AppConfig | null): string {
  return config?.helpUrl?.trim() ?? '';
}

export function getTermsContent(config: AppConfig | null): string {
  return config?.termsContent?.trim() ?? '';
}

export function getPrivacyContent(config: AppConfig | null): string {
  return config?.privacyContent?.trim() ?? '';
}

export function getHelpContent(config: AppConfig | null): string {
  return config?.helpContent?.trim() ?? '';
}

// Missing/loading config: treat signup as enabled; only explicit "false" disables
export function isSignupEnabled(config: AppConfig | null): boolean {
  if (config == null) return true;
  return config.signupEnabled !== 'false';
}

// Missing/loading config: treat as off; only explicit "true" enables
export function isMaintenanceMode(config: AppConfig | null): boolean {
  if (config == null) return false;
  return config.maintenanceMode === 'true';
}

export function getMaintenanceMessage(config: AppConfig | null): string {
  return config?.maintenanceMessage?.trim() ?? '';
}

export function getLoginBanner(config: AppConfig | null): string {
  return config?.loginBanner?.trim() ?? '';
}

export function getRegisterSuccessMessage(config: AppConfig | null): string {
  return config?.registerSuccessMessage?.trim() ?? '';
}

export function getPricingMessage(config: AppConfig | null): string {
  return config?.pricingMessage?.trim() ?? '';
}

export function getPriceLabel(config: AppConfig | null): string {
  return config?.priceLabel?.trim() ?? '';
}

export function getSubscriptionSuccessMessage(config: AppConfig | null): string {
  return config?.subscriptionSuccessMessage?.trim() ?? '';
}

export function getNotificationTitlePrefix(config: AppConfig | null): string {
  return config?.notificationTitlePrefix?.trim() ?? '';
}

// Off by default; only explicit "true" enables
export function isEmailSendingEnabled(config: AppConfig | null): boolean {
  return config?.emailSendingEnabled === 'true';
}

export function getSessionDurationDays(config: AppConfig | null): number | null {
  const s = config?.sessionDurationDays?.trim();
  if (s === '') return null;
  const n = parseInt(s ?? '', 10);
  return Number.isFinite(n) && n >= 1 ? n : null;
}
